// 安心包活动列表页面配置
/**
 * 698 小红书活动状态（**后台文案**）
 *
 * 后端 /axbApp/pileActivityOrderPage 的 xhsActivityStatus 返回的是状态码，且后端 XhsNoteStatusEnum.desc
 * 是**C 端口径**（如「审核通过（公示中）」），后台要「展示中」「审核中」「未通过」，故在此按 code 映射。
 *
 * 注意：后端 join 的是该订单**最新一条 submit 的笔记**，其状态**可为 rejected**（刚被驳回、尚未重提时），
 * 故这里包含 rejected →「未通过」。
 */
export const xhsStatusMap = {
  pending2: "审核中",
  showing: "展示中",
  showing_waiting: "展示中（待发放）",
  granted: "已发放",
  rejected: "未通过",
};

const orderStatusMap = {
  COMMIT: "已报名",
  TO_SIGN: "待签约",
  IN_SIGN: "已签约",
  TO_INSTALL: "待安装",
  TO_ACTIVATION: "待激活",
  IN_USE: "使用中",
  TO_COMPLIANCE: "待守约",
  IN_OVERDUE: "已逾期",
  IN_DEFAULTED: "已违约",
  TO_RENEWED: "待续费",
  IN_CANCELING: "解约申请中",
  IN_CANCELLED_BUYOUT: "已解约(买断)",
  IN_CANCELLED_REFUNDED: "已解约(退出)",
  IN_CANCELLED_DEFAULTED: "已解约(违约)",
  IN_EXPIRED: "已解约(到期)",
  IN_CLOSE: "已退出",
  TO_RESET_SIGN: "待激活签约",
  PAUSE: "暂停使用",
  CANCELLED_NOT_INSTALL: "已解约(未安装退出)",
  BUYOUT_IN_USE_STATUS: "提前买断·使用中",
  HAVING_ERROR_ILLEGAL_BUYOUT: "已解约·违约买断",
  SWAPPING: "换桩中",
};

const pileStatusMap = {
  ACTIVATED: "已激活",
  INVALID: "已失效",
  ACTIVATE_EXCEPTION: "激活异常",
};

const flowStatusMap = {
  COMMON: "正常",
  ABOUT_TO_EXPIRE: "即将到期",
  EXPIRED: "已到期",
  CANCELED: "已解约",
};

const installStatusMap = {
  INVESTIGATION_INSTALL_INIT: "未勘察未安装",
  INVESTIGATION_INSTALL_COMPLETED: "已勘察已安装",
  INVESTIGATION_FINISHED_NOT_INSTALL: "已勘察不安装",
  INVESTIGATION_INIT_NOT_INSTALL: "未勘察不安装",
};

const contractStatusMap = {
  WAIT_CONTRACT: "待联系",
  CONTRACTED: "已联系",
  NOT_CONTRACT: "未联系上",
};

// 流量服务状态（与空开活动保持一致的中文映射）
const actualAmountStatusMap = {
  COMMIT: "已报名",
  TO_SIGN: "待签约",
  IN_SIGN: "已签约",
  TO_INSTALL: "待安装",
  TO_ACTIVATION: "待激活",
  IN_USE: "使用中",
  TO_COMPLIANCE: "待守约",
  IN_OVERDUE: "已逾期",
  IN_DEFAULTED: "已违约",
  IN_CANCELING: "解约申请中",
  IN_CANCELLED_BUYOUT: "已解约(买断)",
  IN_CANCELLED_REFUNDED: "已解约(退出)",
  IN_CANCELLED_DEFAULTED: "已解约(违约)",
  IN_EXPIRED: "已解约(到期)",
  IN_CLOSE: "已退出",
  TO_RESET_SIGN: "待激活签约",
  PAUSE: "暂停使用",
  BUYOUT_IN_USE_STATUS: "提前买断·使用中",
  HAVING_ERROR_ILLEGAL_BUYOUT: "已解约·违约买断",
  // 兼容流量服务常见状态（历史接口字段）
  TO_EXPRIRED: "即将到期",
  TO_EXPIRED: "即将到期",
  EXPIRED: "已到期",
  EXPIRING_SOON: "即将到期",
  TO_ACTIVE: "待激活",
  ACTIVE: "正常",
  IN_ACTIVE: "正常",
  CANCELLED: "已解约（买断）",
  TO_RENEWED: "待续费",
};

let pageConfig = {};

pageConfig.listColumns = [
  {
    label: "活动订单编号",
    props: "activityOrderNo",
    minWidth: 240,
    slot: true,
  },
  {
    label: "姓名",
    props: "name",
    minWidth: 120,
  },
  {
    label: "用户手机号",
    props: "mobile",
    minWidth: 150,
  },
  {
    label: "购买名称",
    props: "purchaseName",
    minWidth: 180,
  },
  {
    label: "订单状态",
    props: "orderStatusName",
    minWidth: 150,
    formatter: (row) => {
      const code = row.orderStatus;
      const name = row.orderStatusName;
      if (orderStatusMap[code]) return orderStatusMap[code];
      if (name === "已解约(退款)") return "已解约(退出)";
      return name || "--";
    },
  }, 
  {
    label: "流量服务状态",
    props: "actualAmountStatus",
    minWidth: 150,
    formatter: (row) => {
      const status = row.actualAmountStatus;
      return actualAmountStatusMap[status] || row.actualAmountStatusName || status || "--";
    },
  },
  {
    label: "流量服务当前开始时间",
    props: "actualAmountStartTime",
    minWidth: 180,
  },
  {
    label: "流量服务当前到期时间",
    props: "actualAmountEndTime",
    minWidth: 180,
  },
  {
    label: "流量服务剩余天数",
    props: "actualAmountDaysRemaining",
    minWidth: 180,
  },
  {
    label: "流量服务累计使用月数",
    props: "actualAmountCumulativeMonths",
    minWidth: 180,
  },
  {
    label: "流量服务累计支付（元）",
    props: "actualAmountTotal",
    minWidth: 180,
  },
  {
    label: "商家代扣状态",
    props: "holdAgreement",
    minWidth: 180,
    formatter: (row) => {
      if (row.holdAgreement === "Y") return "已签约";
      if (row.holdAgreement === "N") return "未签约";
      return row.holdAgreementName || row.holdAgreement || "--";
    },
  },
  {
    label: "首次代扣权益",
    props: "signStatus",
    minWidth: 130,
    formatter: (row) => {
      if (row.signStatus === "Y") return "未开通";
      if (row.signStatus === "N") return "开通";
      return row.signStatusName || "--";
    },
  },
  {
    label: "首次代扣开通时间",
    props: "signTime",
    minWidth: 180,
  },
  {
    label: "客服联系状态",
    props: "activityOrderContractStatusName",
    minWidth: 150,
    formatter: (row) => {
      return contractStatusMap[row.activityOrderContractStatus] || row.activityOrderContractStatusName || "--";
    },
  },
  {
    label: "安装状态",
    props: "activityOrderInstallStatusName",
    minWidth: 150,
    // formatter: (row) => {
    //   return installStatusMap[row.installStatus] || row.installStatusName || "--";
    // },
  },
  {
    label: "不安装/不签约原因",
    props: "installFailReason",
    minWidth: 180,
  },
  {
    label: "产品编号",
    props: "pileCode",
    minWidth: 180,
  },
  {
    label: "充电桩资产编号",
    props: "pileAssetCode",
    minWidth: 180,
  },
  {
    label: "报名创建时间",
    props: "createdWhen",
    minWidth: 180,
  },
  {
    label: "关联主服订单号",
    props: "pileInstallOrderNo",
    minWidth: 180,
  },
  {
    label: "充电桩安装完成时间",
    props: "pileInstallCompletedTime",
    minWidth: 180,
  },
  {
    label: "充电桩绑定激活时间",
    props: "bindTime",
    minWidth: 180,
  },
  {
    label: "激活切换操作人",
    props: "",
    minWidth: 180,
  },
  {
    label: "激活切换操作时间",
    props: "",
    minWidth: 180,
  },
  {
    label: "解约申请时间",
    props: "cancelRequestTime",
    minWidth: 180,
  },
  {
    label: "解约完成时间",
    props: "cancelFinishTime",
    minWidth: 180,
  },
  {
    label: "解约原因",
    props: "cancelReason",
    minWidth: 180,
  },
  {
    label: "解约应付金额（元）",
    props: "cancelAmount",
    minWidth: 180,
  },
  {
    label: "解约应退金额（元）",
    props: "refundAmount",
    minWidth: 180,
  },

  {
    label: "是否买断回退",
    props: "buyoutOrNot",
    minWidth: 180,
  },
  {
    label: "回退金额（元）",
    props: "rebackedAmount",
    minWidth: 180,
  },
  {
    label: "解约退款操作人",
    props: "rebackedAmountOperatePer",
    minWidth: 180,
  },
  {
    label: "解约退款操作时间",
    props: "refundOperateTime",
    minWidth: 180,
  },
  {
    label: "解约退款完成时间",
    props: "refundFinishTime",
    minWidth: 180,
  },
  {
    label: "省",
    props: "province",
    minWidth: 180,
  },
  {
    label: "市",
    props: "city",
    minWidth: 180,
  },
  {
    label: "区",
    props: "area",
    minWidth: 180,
  },
  // ===== 698 小红书分享活动（后端按 activityOrderNo 关联笔记表带出；已排除未通过历史记录）=====
  {
    label: "小红书活动状态",
    props: "xhsActivityStatus",
    minWidth: 150,
    formatter: (row) =>
      xhsStatusMap[row.xhsActivityStatus] || (row.xhsActivityStatus ? row.xhsActivityStatus : "-"),
  },
  {
    label: "实际奖励月数",
    props: "xhsGrantMonths",
    minWidth: 115,
  },
];

pageConfig.listBreadCrumb = [
  {
    type: "title",
    name: "首页",
  },
  {
    type: "title",
    name: "营销活动",
  },
  {
    type: "title",
    name: "安心包598活动",
  },
  {
    type: "title",
    name: "安心包598活动服务列表",
  },
];

pageConfig.tableConfig = {
  showSelection: true,
  showIndex: false,
};

pageConfig.pagination = {
  current: 1,
  total: 0,
  pageSize: 10,
  pageSizes: [10, 20, 50, 100],
};

pageConfig.orderStatusMap = orderStatusMap;
pageConfig.pileStatusMap = pileStatusMap;
pageConfig.flowStatusMap = flowStatusMap;
pageConfig.installStatusMap = installStatusMap;
pageConfig.contractStatusMap = contractStatusMap;
pageConfig.actualAmountStatusMap = actualAmountStatusMap;

export default pageConfig;
